import { Result } from "@/api/types";
import { UserTypeContext } from "@/providers/UserTypeProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useContext } from "react";
import useSmartQueryClient from "./useSmartQueryClient";

export type UserRole = "root_admin" | "root_user" | "company_admin" | "user";

export interface UserSettings {
  dashboard_start_date: string | null;
  dashboard_end_date: string | null;
  dashboard_year: number;
  latest_release_notes_id: string;
  user_name: string;
  user_id: number;
  company_id: number;
  role: UserRole;
  email: string;
}

export interface PatchUserSettings {
  dashboard_start_date?: string;
  dashboard_end_date?: string;
  dashboard_year?: number;
  latest_release_notes_id?: string;
}

export function useUserSettings() {
  const { userType } = useContext(UserTypeContext);

  const query = useQuery({
    queryKey: ["user_settings"],
    enabled: userType === "internal",
    queryFn: () =>
      axios.get<Result<UserSettings>>("user_settings").then((data) => data.data.result),
  });

  return query;
}

export function usePatchUserSettings() {
  const smartQueryClient = useSmartQueryClient();

  const query = useMutation({
    mutationKey: ["user_settings", "patch"],
    onMutate: (data) => {
      smartQueryClient.setQueryData<UserSettings>(["user_settings"], (userSettings) => {
        if (!userSettings) return undefined;
        const newUserSettings = {
          ...userSettings,
        };
        if (data.dashboard_start_date !== undefined)
          newUserSettings.dashboard_start_date = data.dashboard_start_date;
        if (data.dashboard_end_date !== undefined)
          newUserSettings.dashboard_end_date = data.dashboard_end_date;
        if (data.dashboard_year !== undefined) newUserSettings.dashboard_year = data.dashboard_year;
        if (data.latest_release_notes_id !== undefined)
          newUserSettings.latest_release_notes_id = data.latest_release_notes_id;
        return newUserSettings;
      });
    },
    mutationFn: (userSettings: PatchUserSettings) =>
      axios.patch<PatchUserSettings>("user_settings", userSettings),
  });

  return query;
}
